import { FaceLandmarker, FilesetResolver, type FaceLandmarkerResult, type NormalizedLandmark } from '@mediapipe/tasks-vision'
import { HeadMatrixNormalizer, mat4, type FaceFrame, type MatrixLayout } from '@ar-suite/core'

export interface FaceTrackerOptions {
  /** Directory containing the self-hosted MediaPipe wasm files (vision_wasm_internal.js/.wasm …). */
  wasmPath: string
  /** URL of face_landmarker.task. */
  modelPath: string
  delegate: 'GPU' | 'CPU'
  numFaces: number
  minFaceDetectionConfidence: number
  minFacePresenceConfidence: number
  minTrackingConfidence: number
  outputBlendshapes: boolean
  /** Force a matrix layout instead of auto-detecting. */
  matrixLayout?: MatrixLayout
}

export const FACE_TRACKER_DEFAULTS: FaceTrackerOptions = {
  wasmPath: '/wasm',
  modelPath: '/models/face_landmarker.task',
  delegate: 'GPU',
  numFaces: 1,
  minFaceDetectionConfidence: 0.5,
  minFacePresenceConfidence: 0.5,
  minTrackingConfidence: 0.5,
  outputBlendshapes: false,
}

export interface FaceTrackerResult {
  faces: FaceFrame[]
  /** Time spent in the detector (ms). */
  inferenceMs: number
}

/**
 * MediaPipe Face Landmarker wrapper producing portable `FaceFrame`s
 * (packed landmarks + column-major head matrix in cm).
 */
export class FaceTracker {
  readonly options: FaceTrackerOptions
  private landmarker: FaceLandmarker | null = null
  private normalizer = new HeadMatrixNormalizer()
  private lastTimestamp = -1
  private frames: FaceFrame[] = []
  private blendshapeNames: string[] | null = null

  constructor(options: Partial<FaceTrackerOptions> = {}) {
    this.options = { ...FACE_TRACKER_DEFAULTS, ...options }
    if (this.options.matrixLayout) this.normalizer.setLayout(this.options.matrixLayout)
  }

  get ready(): boolean {
    return this.landmarker !== null
  }
  get matrixLayout(): MatrixLayout {
    return this.normalizer.layout
  }

  async init(): Promise<void> {
    if (this.landmarker) return
    const vision = await FilesetResolver.forVisionTasks(this.options.wasmPath)
    const create = (delegate: 'GPU' | 'CPU') =>
      FaceLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: this.options.modelPath, delegate },
        runningMode: 'VIDEO',
        numFaces: this.options.numFaces,
        minFaceDetectionConfidence: this.options.minFaceDetectionConfidence,
        minFacePresenceConfidence: this.options.minFacePresenceConfidence,
        minTrackingConfidence: this.options.minTrackingConfidence,
        outputFaceBlendshapes: this.options.outputBlendshapes,
        outputFacialTransformationMatrixes: true,
      })
    try {
      this.landmarker = await create(this.options.delegate)
    } catch (e) {
      if (this.options.delegate === 'GPU') {
        console.warn('[ar-suite] GPU delegate failed, falling back to CPU', e)
        this.landmarker = await create('CPU')
      } else throw e
    }
  }

  /**
   * Run detection on the current video frame.
   * @param timestampMs monotonically increasing timestamp (use performance.now() or the video frame's mediaTime*1000)
   */
  detect(video: HTMLVideoElement | HTMLCanvasElement | ImageBitmap | OffscreenCanvas, timestampMs: number): FaceTrackerResult {
    if (!this.landmarker) throw new Error('FaceTracker.init() has not completed')
    // MediaPipe requires strictly increasing timestamps
    if (timestampMs <= this.lastTimestamp) timestampMs = this.lastTimestamp + 1
    this.lastTimestamp = timestampMs
    const t0 = performance.now()
    const result: FaceLandmarkerResult = this.landmarker.detectForVideo(video, timestampMs)
    const inferenceMs = performance.now() - t0
    const width = 'videoWidth' in video ? video.videoWidth : video.width
    const height = 'videoHeight' in video ? video.videoHeight : video.height
    const n = result.faceLandmarks.length
    for (let i = 0; i < n; i++) {
      const lms = result.faceLandmarks[i]!
      let frame = this.frames[i]
      if (!frame || frame.landmarks.length !== lms.length * 3) {
        frame = { landmarks: new Float32Array(lms.length * 3), headMatrix: mat4.create(), width, height, timestamp: timestampMs }
        this.frames[i] = frame
      }
      packLandmarks(frame.landmarks, lms)
      const m = result.facialTransformationMatrixes[i]
      if (m) this.normalizer.normalize(frame.headMatrix, m.data)
      frame.width = width
      frame.height = height
      frame.timestamp = timestampMs
      if (this.options.outputBlendshapes) {
        const cats = result.faceBlendshapes[i]?.categories
        if (cats) {
          const bs: Record<string, number> = frame.blendshapes ?? {}
          for (const c of cats) bs[c.categoryName] = c.score
          frame.blendshapes = bs
          this.blendshapeNames ??= cats.map((c) => c.categoryName)
        }
      }
    }
    return { faces: this.frames.slice(0, n), inferenceMs }
  }

  /** Face-mesh triangle edges — used to build the occluder mesh. */
  static get tesselation(): { start: number; end: number }[] {
    return FaceLandmarker.FACE_LANDMARKS_TESSELATION
  }

  close(): void {
    this.landmarker?.close()
    this.landmarker = null
  }
}

function packLandmarks(out: Float32Array, lms: NormalizedLandmark[]): void {
  for (let i = 0; i < lms.length; i++) {
    const l = lms[i]!
    out[i * 3] = l.x
    out[i * 3 + 1] = l.y
    out[i * 3 + 2] = l.z
  }
}
